import { Alert, AlertDescription, Button, useToast } from "@chakra-ui/react";
import React, { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useModalStore } from "../../hooks/modal.hooks";
import { useSidebarStore } from "../../hooks/sidebar.hooks";
import { useKnowledgeBaseStore } from "../../hooks/knowledgeBase.hooks";
import { useCollectiveAgreements } from "../../networking/collective-agreement.hooks";
import { useCustomers } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSyncVersionWatcher } from "../../networking/sync-version.hooks";
import { useSpaVersion } from "../../networking/version.hooks";
import { checkNavigationMenuVisibility } from "../../utils/route.utils";
import SSE from "../Core/SSE/SSE";
import AbsenceModal from "../Shared/AbsenceModal/AbsenceModal";
import ConfirmationModal from "../Shared/ConfirmationModal/ConfirmationModal";
import MessageModal from "../Shared/MessageModal/MessageModal";
import Sidebar from "../Shared/Sidebar/Sidebar";
import SideMenu from "../Shared/SideMenu/SideMenu";
import SplitView from "../Shared/SplitView/SplitView";
import KnowledgeBaseSidebar from "../Shared/KnowledgeBase/KnowledgeBaseSidebar";
import { AiAssistantSidebar } from "../Shared/AiAssistant";
import { useReportBuilderStore } from "../../hooks/reportBuilder.hooks";
import { useAuthStore } from "../../stores/auth.store";
import { useConfirmModalStore } from "../../stores/confirmModal.store";
import { useSplitViewStore } from "../../stores/splitView.store";

interface LoggedInLayoutProps {
	children?: ReactNode;
}

const LoggedInLayout: React.FC<LoggedInLayoutProps> = ({ children }) => {
	const { t } = useTranslation();
	const toast = useToast();

	const containerRef = useRef<HTMLDivElement>(null);

	const { data: version } = useSpaVersion();

	const modalStore = useConfirmModalStore();
	const splitViewStore = useSplitViewStore();
	const authStore = useAuthStore();
	const { showModal } = modalStore;
	const { showSplitView } = splitViewStore;
	const location = useLocation();
	const { showAbsenceFor, showMessageFor } = useModalStore((state) => ({
		showAbsenceFor: state.showAbsenceFor,
		showMessageFor: state.showMessageFor,
	}));
	const { activeSidebar, sidebarProps } = useSidebarStore((state) => ({
		activeSidebar: state.activeSidebar,
		sidebarProps: state.sidebarProps,
	}));
	const { isOpen: isKnowledgeBaseOpen } = useKnowledgeBaseStore();
	const tvMode = useReportBuilderStore((state) => state.tvMode);

	useEffect(() => {
		const toastId = "version-check";
		if (
			version &&
			window.gitVersion &&
			version.gitVersion !== window.gitVersion &&
			!toast.isActive(toastId)
		) {
			toast({
				id: "version-check",
				render: () => (
					<Alert status="success" borderWidth={2} variant={"solid"}>
						<AlertDescription>
							{t("updateToast.title")}
							<br />
							{t("updateToast.description")}
						</AlertDescription>
						<Button size={"sm"} onClick={handleReload} colorScheme="whiteAlpha" ml="2">
							{t("updateToast.button")}
						</Button>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 30 * 1000,
			});
		}
	}, [version]);

	const { data } = useOrganisation(); // preload and cache as soon as possible

	// Poll the cheap sync-version endpoints and invalidate the persisted full
	// consultant/customer lists only when server data actually changed.
	useSyncVersionWatcher();

	// Preload collective agreements for use throughout the app
	useCollectiveAgreements({
		page: 0,
		pageSize: 100,
	});

	const { customers } = useCustomers({
		page: 1,
		pageSize: 100,
		active: false,
		isSignup: true,
	});

	const badges = {
		CUSTOMERS: {
			count: customers?.totalCount || 0,
			tooltip: t("customer.numInactiveSignupsTooltip"),
		},
	};

	function handleReload() {
		// Append a timestamp or the new Git hash as a query parameter to ensure the page reloads from the server
		const newUrl = window.location.href.split("?")[0] + "?v=" + new Date().getTime();
		window.location.href = newUrl;
	}

	useEffect(() => {
		if (data?.dashboardScript) {
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.innerHTML = data.dashboardScript;
			containerRef.current?.appendChild(script);
		}
	}, [data]);

	return (
		<div className="flex w-screen h-screen overflow-hidden bg-[#f2f2f2]">
			{/* SSE component for handling server-sent events */}
			{!!authStore.accessToken && !!authStore.orgId && (
				<SSE organisationId={authStore.orgId} accessToken={authStore.accessToken} />
			)}
			{showModal && <ConfirmationModal />}
			{showSplitView && <SplitView />}
			{!tvMode && !checkNavigationMenuVisibility(location.pathname) && (
				<SideMenu scopes={authStore.scopes} badges={badges} />
			)}
			<div className="relative min-w-0 flex-1 overflow-y-auto">{children}</div>
			{!tvMode && <AiAssistantSidebar />}
			{activeSidebar && <Sidebar type={activeSidebar} sidebarProps={sidebarProps} />}
			{isKnowledgeBaseOpen && <KnowledgeBaseSidebar />}
			{showAbsenceFor && <AbsenceModal {...showAbsenceFor} />}
			{showMessageFor && <MessageModal {...showMessageFor} />}

			{data?.dashboardScript && <div ref={containerRef} />}
		</div>
	);
};

export default LoggedInLayout;
